import React, { useState, useEffect, useMemo } from 'react';
import { cleanPkString } from 'utils/bitacora';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Modal, Table, Typography, Tag,
} from 'antd';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import useCatalogos from 'hooks/useCatalogos';
import { getContribuyente } from 'api/empresas/contribuyente';

const { Text } = Typography;

const CAMPOS_OCULTOS = new Set([
  'json_predio', 'participe_principal', 'auxiliar_base_datos', 'procedencia',
  'accionInf', 'contributente', 'xml_archivo', 'cuenta_unica', 'corte',
  'content_type_padron', 'datos_del_recibo', 'id', 'key', 'clave_catastral_municipal',
  'tipo_de_empresa', 'padrones', 'json_bool', 'billing', 'currency', 'paymentMethod',
  'padron_id', 'idPadron', 'ciudadano', 'metodos_de_pago', 'json_reincidencias',
  'moneda', 'tipo_de_padron', 'tipo_padron_id', 'tipos_de_registros_de_tramite',
  'content_type_a_padron', 'object_id_padron', 'content_type', 'object_id',
]);

const DECIMAL_KEYS = new Set([
  'valor_m2_del_terreno', 'valor_m2_del_terreno_anterior', 'valor_catastral',
  'valor_catastral_anterior', 'base_gravable', 'base_gravable_anterior',
  'impuesto_predial', 'factor_merito_demerito', 'superficie_de_la_construccion',
  'superficie_total_del_terreno', 'porcentaje_de_multa', 'porcentaje_de_recargo',
  'factura', 'importe_total', 'operacion_de_pago', 'redondeo',
  'importe', 'adeudo_base', 'adeudo_total', 'importe_de_moneda',
  'total_de_abonos_base',
  'cantidad_descuento', 'total_abonado', 'saldo_pendiente_del_descuento',
]);

const ESTADOS_DEL_DESCUENTO = Object.freeze({
  VIGENTE: 1, APLICADO: 2, CANCELADO: 3, VENCIDO: 4,
});

const CATALOG_FIELDS = [
  { key: 'regimen_de_propiedad', getCat: (c) => c.regimenes, keys: ['descripcion'] },
  { key: 'uso_de_predio', getCat: (c) => c.usosPredio, keys: ['descripcion'] },
  { key: 'tipo_de_aseo', getCat: (c) => c.aseoPredio, keys: ['descripcion'] },
  { key: 'tipo_de_predio', getCat: (c) => c.tipoPredio, keys: ['descripcion'] },
  { key: 'zona', getCat: (c) => c.zonaPredio, keys: ['descripcion'] },
  { key: 'estado_de_la_infraccion', getCat: (c) => c.estadoInfraccion, keys: ['descripcion'] },
  {
    key: 'tipo_de_concepto_de_infraccion',
    getCat: (c) => c.tipoConcepto,
    keys: ['descripcion'],
  },
  {
    key: 'periodo_fiscal',
    getCat: (c) => c.periodosFiscales,
    keys: ['periodo', 'descripcion'],
  },
  {
    key: 'regimen_fiscal',
    getCat: (c) => c.periodosFiscales,
    keys: ['periodo', 'descripcion'],
  },
  { key: 'clase_de_vehiculo', getCat: (c) => c.clasesVehiculo },
  { key: 'clase_vehicular', getCat: (c) => c.clasesVehiculo },
  { key: 'uso_de_motocicleta', getCat: (c) => c.usosMotocicleta },
  { key: 'uso_del_vehiculo', getCat: (c) => c.usosMotocicleta },
  {
    key: 'permiso_de_tipo_de_placa',
    getCat: (c) => c.permisosTipoPlaca,
    keys: ['descripcion', 'nombre', 'permiso', 'clave'],
  },
  {
    key: 'permiso_tipo_de_placa',
    getCat: (c) => c.permisosTipoPlaca,
    keys: ['descripcion', 'nombre', 'permiso', 'clave'],
  },
  { key: 'tipo_de_establecimiento', getCat: (c) => c.tipoEstablecimiento },
  { key: 'zona_de_modulo', getCat: (c) => c.mercadosModulos },
  { key: 'clase_de_anuncio', getCat: (c) => c.clasesAnuncio },
  { key: 'espacio', getCat: (c) => c.tiposEspacio },
  { key: 'tipo_contrato', getCat: (c) => c.tiposContrato },
  { key: 'panteon', getCat: (c) => c.tiposPanteon },
  { key: 'tipo_de_anuncio', getCat: (c) => c.tiposAnuncio },
  { key: 'tipo_de_infractor', getCat: (c) => c.tiposInfractores },
];

// ─── Pure utilities ───────────────────────────────────────────────────────────
const mkFmt = (cat, keys = ['descripcion', 'nombre', 'clave']) => (v) => {
  if (!v && v !== 0) return '—';
  const limpio = cleanPkString(v);
  const pick = (item) => { const k = keys.find((c) => item[c]); return k ? item[k] : null; };
  // Lookup by numeric ID
  const num = Number(limpio);
  if (!Number.isNaN(num)) {
    const byId = (cat || []).find((x) => String(x.id) === String(num));
    if (byId) return pick(byId) ?? num;
  }
  // Lookup by exact text match in any of the catalog key fields
  const txt = String(limpio ?? '').toLowerCase().trim();
  const byText = (cat || []).find((x) => (
    keys.some((c) => x[c] && String(x[c]).toLowerCase().trim() === txt)
  ));
  if (byText) return pick(byText) ?? txt;
  // "CLAVE - DESCRIPCION" format: look up the item whose clave matches the part before ' - '
  const claveMatch = String(limpio ?? '').match(/^([A-Za-z0-9]+)\s*-\s+/);
  if (claveMatch) {
    const byClave = (cat || []).find((x) => (
      x.clave && String(x.clave).trim().toLowerCase() === claveMatch[1].toLowerCase()
    ));
    if (byClave) return pick(byClave) ?? limpio;
  }
  if (limpio === null || limpio === undefined || limpio === '') return '—';
  return limpio;
};
const mkNorm = (fn) => (v) => String(fn(v) ?? '').trim().toLowerCase();

const fmt2 = (valor) => {
  if (valor === null || valor === undefined) return '—';
  const n = Number(String(valor).replace(/,/g, ''));
  if (!Number.isFinite(n)) return String(valor);
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const detectarAccionInf = (final = {}, inicial = {}) => (
  final?.accionInf || inicial?.accionInf || null
);

const boolTag = (val, siColor = 'processing') => (
  val ? <Tag color={siColor}>Sí</Tag> : <Tag color="default">No</Tag>
);

const getCamposUnicos = (obj1, obj2) => Array.from(new Set([
  ...Object.keys(obj1 || {}), ...Object.keys(obj2 || {}),
]));

const labelDeCampo = (key) => (
  key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
);

const procesarCampos = (keys, mapFn) => keys
  .filter((key) => !CAMPOS_OCULTOS.has(key))
  .map(mapFn)
  .filter(Boolean)
  .sort((a, b) => a.campo.localeCompare(b.campo, 'es', { sensitivity: 'base' }));

const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s || s.toLowerCase() === 'none') return null;
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

const parseBoolLoose = (v) => {
  if (typeof v === 'boolean') return v;
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true') return true;
    if (s === 'false') return false;
  }
  return null;
};

const formatDateOnly = (valor) => {
  if (typeof valor !== 'string') return valor;
  if (/^\d{4}-\d{2}-\d{2}T/.test(valor)) return valor.split('T')[0];
  return valor;
};

const fallbackNormalize = (value) => {
  const b = parseBoolLoose(value);
  if (b !== null) return String(b);
  if (typeof value === 'string') {
    const cleaned = String(formatDateOnly(cleanPkString(value)) ?? '').trim();
    // Normalize formatted numbers (e.g. "946,500.34") to plain form for comparison
    const numStr = cleaned.replace(/,/g, '');
    const n = Number(numStr);
    if (numStr !== '' && Number.isFinite(n)) return String(n);
    return cleaned;
  }
  if (typeof value === 'object' && value !== null) {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value ?? '');
};

const extractId = (v) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const mPk = v.match(/Pk:\s*(\d+)/i);
    if (mPk) return Number(mPk[1]);
    const mNum = v.trim().match(/^\d+$/);
    if (mNum) return Number(mNum[0]);
  }
  return null;
};

const extractRFC = (v) => {
  if (!v && v !== 0) return null;
  if (typeof v === 'object') {
    const r = v.rfc || v.RFC || v.rfc_contribuyente;
    return r ? String(r).trim().toUpperCase() : null;
  }
  if (typeof v === 'string') {
    const m = v.match(/RFC:\s*([A-Z0-9]{10,13})/i);
    return m ? m[1].toUpperCase() : null;
  }
  return null;
};

const parseNotarioInfo = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return { id: v, nombre: null, clave: null };
  const s = String(v);
  const mId = s.match(/Pk:\s*(\d+)/i) || s.match(/^\s*(\d+)\s*$/);
  const mNombre = s.match(/Descripci[oó]n:\s*([^|]+)/i);
  const mClave = s.match(/Clave:\s*([^|]+)/i);
  const id = mId ? Number(mId[1] || mId[0]) : null;
  const nombre = mNombre ? mNombre[1].trim() : null;
  const clave = mClave ? mClave[1].trim() : null;
  return (id || nombre || clave) ? { id, nombre, clave } : null;
};

const formatIdPadron = (valor) => {
  if (!valor) return '—';
  const entry = Object.entries(padronesEnum).find(([, id]) => String(id) === String(valor));
  return entry ? entry[0].replace(/_/g, ' ').toLowerCase() : valor;
};

const tryParseJSON = (v) => {
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch { return v; }
};

const joinNonEmpty = (parts, sep = ', ') => (
  parts.map((p) => String(p || '').trim()).filter(Boolean).join(sep)
);

const buildDireccionString = (d) => {
  if (!d || typeof d !== 'object') return String(d ?? '—');
  const numInt = d.numero_interior ? `Int. ${d.numero_interior}` : '';
  const colonia = d.colonia_texto || d.colonia || d.asentamiento;
  const cpObj = (d.codigo_postal && typeof d.codigo_postal === 'object') ? d.codigo_postal : null;
  let cp = d.cp_texto ?? null;
  if (!cp) {
    if (typeof d.codigo_postal === 'number' || typeof d.codigo_postal === 'string') {
      cp = d.codigo_postal;
    } else if (d.cp) cp = d.cp;
  }
  const l1 = joinNonEmpty([d.calle_principal, d.numero_exterior, numInt], ' ');
  const l2 = joinNonEmpty([
    colonia || cpObj?.d_asenta,
    d.municipio_texto || d.municipio || cpObj?.municipio?.nom_mun,
    d.estado_texto || d.estado || cpObj?.municipio?.estado?.nombre_de_AGEE,
  ]);
  const l3 = cp ? `C.P. ${cp}` : '';
  const extras = joinNonEmpty([d.agencia, d.entre_calle, d.y_calle], ' / ');
  return joinNonEmpty([l1 || null, l2 || null, l3 || null, extras || null]);
};

const canonDireccion = (s) => {
  if (s == null) return '';
  return String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[.,;:/\-|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const formatContribuyenteValue = (valor, cache) => {
  if (valor === null || valor === undefined) return '—';
  if (typeof valor === 'object') {
    const nombre = valor.razon_social || valor.nombre_completo
      || valor.nombre || valor.descripcion;
    const rfc = valor.rfc || valor.RFC || valor.rfc_contribuyente;
    return [nombre, rfc, valor.clave].filter(Boolean).join(' | ') || '—';
  }
  const id = extractId(valor);
  if (id && cache?.[id]) {
    const c = cache[id];
    const nombre = c.razon_social || c.nombre_completo || c.nombre || c.descripcion;
    const rfc = c.rfc || c.RFC || c.rfc_contribuyente;
    return [nombre, rfc, c.clave].filter(Boolean).join(' | ') || id;
  }
  if (typeof valor === 'string') return cleanPkString(valor) || '—';
  return String(valor);
};

const formatJsonPredio = (raw) => {
  const obj = tryParseJSON(raw);
  if (!obj || typeof obj !== 'object') return '—';
  const propietario = obj.nombre_completo
    || [obj.apellido_paterno_propietario, obj.nombre_propietario].filter(Boolean).join(' ')
    || '—';
  const attrs = obj.attrs || {};
  const campos = [
    ['Clave', obj.clave],
    ['Clave catastral (estándar)', obj.clave_catastral_estandar],
    ['Clave catastral (municipal)', obj.clave_catastral_municipal],
    ['Propietario', propietario],
    ['Uso de predio', obj.uso_de_predio],
    ['Régimen de propiedad', obj.regimen_de_propiedad],
    ['Colonia', attrs.colonia],
    ['Agencia', attrs.agencia],
    ['Zona', attrs.zona],
  ];
  return (
    <div style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>
      {campos.map(([label, val]) => (
        <div key={label} style={{ marginBottom: 4 }}>
          <strong>
            {label}
            :
          </strong>
          {' '}
          {val ?? '—'}
        </div>
      ))}
    </div>
  );
};

const formatearValor = (valor) => {
  if (valor === null || valor === undefined || valor === 'None') return '—';
  const asBool = parseBoolLoose(valor);
  if (asBool !== null) return asBool ? 'Sí' : 'No';
  if (typeof valor === 'string') {
    const soloFecha = formatDateOnly(cleanPkString(valor));
    if (soloFecha === '') return '—';
    // Reformat numeric strings with 3+ decimal places to 2 (e.g. "490404.500000…")
    if (/^-?\d+\.\d{3,}$/.test(soloFecha.replace(/,/g, ''))) {
      const n = Number(soloFecha.replace(/,/g, ''));
      if (Number.isFinite(n)) {
        return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    }
    // "N - DESCRIPTION" (no catalog available): return only the description part
    const numDash = soloFecha.match(/^\d+\s*-\s+(.+)$/);
    if (numDash) return numDash[1].trim();
    return soloFecha;
  }
  if (Array.isArray(valor)) {
    if (valor.length === 0) return '—';
    if (valor[0]?.descripcion) {
      return valor.map((p) => p.descripcion || p.nombre_completo).join(', ');
    }
    return valor.map((v) => formatearValor(v)).join(', ');
  }
  if (typeof valor === 'object') {
    if (valor.calle_principal || valor.cp_texto || valor.codigo_postal
      || valor.municipio_texto || valor.estado_texto) {
      return buildDireccionString(valor) || '—';
    }
    if (valor.nombre_completo || valor.curp || valor.clave) {
      const curp = valor.curp ? ` | ${valor.curp}` : '';
      const clave = valor.clave ? ` | ${valor.clave}` : '';
      return `${valor.nombre_completo || ''}${curp}${clave}`;
    }
    return JSON.stringify(valor);
  }
  if (typeof valor === 'number') {
    if (!Number.isFinite(valor)) return '—';
    if (!Number.isInteger(valor)) {
      return valor.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
  return String(valor);
};

// ─── Column definitions (module-level constants, no useMemo needed) ───────────
const COLUMNAS_CAMPO_VALOR = [
  {
    title: 'Campo',
    dataIndex: 'campo',
    key: 'campo',
    width: '35%',
    render: (value, row) => (row.esSeccion
      ? (
        <Text
          strong
          style={{
            color: '#888', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase',
          }}
        >
          {value}
        </Text>
      )
      : <Text strong>{value}</Text>),
  },
  {
    title: 'Valor',
    dataIndex: 'valor',
    key: 'valor',
    width: '65%',
    render: (value, row) => {
      if (row.esSeccion) return null;
      return typeof value === 'object' && value !== null
        ? value : <Text>{value ?? '—'}</Text>;
    },
  },
];

const COLUMNAS_COMPARACION = [
  {
    title: 'Campo',
    dataIndex: 'campo',
    key: 'campo',
    width: '30%',
    render: (value) => <Text strong>{value}</Text>,
  },
  {
    title: 'Valor Inicial',
    dataIndex: 'valor_inicial',
    key: 'valor_inicial',
    width: '35%',
    render: (value) => (typeof value === 'object' ? value : <Text>{value}</Text>),
  },
  {
    title: 'Valor Final',
    dataIndex: 'valor_final',
    key: 'valor_final',
    width: '35%',
    render: (value, row) => {
      const content = typeof value === 'object' ? value : <>{value}</>;
      return row.haCambiado
        ? <Text type="danger">{content}</Text>
        : <Text>{content}</Text>;
    },
  },
];

// ─── Transform functions ──────────────────────────────────────────────────────
const resolverNombreUsuario = (idUsuario, dataAnterior = {}) => {
  if (!idUsuario) return '—';
  const usuarioCreoStr = dataAnterior?.usuario_creo || '';
  const matchPk = usuarioCreoStr.match(/Pk:\s*(\d+)/i);
  if (matchPk && String(matchPk[1]) === String(idUsuario)) {
    const matchNombre = usuarioCreoStr.match(/Nombre:\s*([^|]+)/i);
    if (matchNombre) return matchNombre[1].trim();
  }
  return `Usuario #${idUsuario}`;
};

const extraerTipoCargo = (tipoDeCargo) => {
  if (!tipoDeCargo) return null;
  if (typeof tipoDeCargo === 'object') {
    return {
      descripcion: tipoDeCargo.descripcion || tipoDeCargo.descripcion_cargo || null,
      clave: tipoDeCargo.clave || null,
      sistema: tipoDeCargo.sistemas?.descripcion || null,
      periodoFiscal: tipoDeCargo.periodo_fiscal?.periodo || null,
    };
  }
  if (typeof tipoDeCargo === 'string') {
    const desc = tipoDeCargo.match(/Descripcion:\s*([^|]+)/i)?.[1]?.trim() || null;
    const clave = tipoDeCargo.match(/Clave:\s*\(([^)]+)\)/i)?.[1]?.trim() || null;
    return {
      descripcion: desc, clave, sistema: null, periodoFiscal: null,
    };
  }
  return null;
};

const transformarCancelacion = (inicial = {}, final = {}) => {
  const tipoCargo = extraerTipoCargo(inicial?.tipo_de_cargo);
  const nombreUsuarioCancelo = resolverNombreUsuario(final?.usuario_que_cancelo, inicial);

  const infoCargo = [
    {
      key: '_header_cargo', campo: 'INFORMACIÓN DEL CARGO', valor: null, esSeccion: true,
    },
    tipoCargo?.descripcion && {
      key: 'desc_tipo_cargo', campo: 'Tipo de Cargo', valor: tipoCargo.descripcion,
    },
    tipoCargo?.clave && {
      key: 'clave_tipo_cargo',
      campo: 'Clave',
      valor: <Tag color="blue">{tipoCargo.clave}</Tag>,
    },
    tipoCargo?.sistema && { key: 'sistema_cargo', campo: 'Sistema', valor: tipoCargo.sistema },
    inicial?.descripcion && {
      key: 'descripcion_cargo', campo: 'Descripción del Cargo', valor: inicial.descripcion,
    },
    (inicial?.importe !== null && inicial?.importe !== undefined) && {
      key: 'importe_cargo',
      campo: 'Importe',
      valor: (
        <Text strong style={{ fontSize: 15 }}>
          $
          {fmt2(inicial.importe)}
          {' '}
          <Text type="secondary" style={{ fontSize: 12 }}>MXP</Text>
        </Text>
      ),
    },
    inicial?.periodo_fiscal && {
      key: 'periodo_fiscal_cargo',
      campo: 'Período Fiscal',
      valor: String(inicial.periodo_fiscal),
    },
    inicial?.fecha_de_creacion && {
      key: 'fecha_creacion_cargo',
      campo: 'Fecha de Creación',
      valor: new Date(inicial.fecha_de_creacion).toLocaleString('es-MX'),
    },
    inicial?.usuario_creo && {
      key: 'usuario_creo_cargo',
      campo: 'Creado Por',
      valor: cleanPkString(inicial.usuario_creo) || inicial.usuario_creo,
    },
    inicial?.observaciones && inicial.observaciones !== 'None' && {
      key: 'observaciones_cargo',
      campo: 'Observaciones del Cargo',
      valor: inicial.observaciones,
    },
  ].filter(Boolean);

  const infoCancelacion = [
    {
      key: '_header_cancelacion',
      campo: 'DATOS DE LA CANCELACIÓN',
      valor: null,
      esSeccion: true,
    },
    {
      key: 'estado_cancelacion',
      campo: 'Estado',
      valor: <Tag color="error" style={{ fontWeight: 600 }}>CANCELADO</Tag>,
    },
    final?.prescripto !== undefined && {
      key: 'prescripto',
      campo: 'Prescripto',
      valor: boolTag(final.prescripto, 'warning'),
    },
    final?.fecha_de_cancelado && {
      key: 'fecha_cancelacion',
      campo: 'Fecha de Cancelación',
      valor: new Date(final.fecha_de_cancelado).toLocaleString('es-MX'),
    },
    { key: 'usuario_cancelo', campo: 'Cancelado Por', valor: nombreUsuarioCancelo },
    final?.observaciones_de_cancelado && {
      key: 'obs_cancelacion', campo: 'Observaciones', valor: final.observaciones_de_cancelado,
    },
  ].filter(Boolean);

  return [...infoCargo, ...infoCancelacion];
};

const transformarDescuento = (final = {}, salida = {}) => {
  const filasVariables = (final.variables || []).map((v) => ({
    key: `variable_${v.id}`,
    campo: v.nombre_de_variable || `Variable ${v.id}`,
    valor: v.valor ?? '—',
  }));

  return [
    {
      key: '_header_descuento',
      campo: 'INFORMACIÓN DEL DESCUENTO',
      valor: null,
      esSeccion: true,
    },
    (salida.descripcion || final.descripcion) && {
      key: 'descripcion_desc',
      campo: 'Descripción',
      valor: salida.descripcion || final.descripcion,
    },
    (final.tipo_de_descuento !== null && final.tipo_de_descuento !== undefined) && {
      key: 'tipo_descuento',
      campo: 'Tipo de Descuento',
      valor: <Tag color="blue">{final.tipo_de_descuento}</Tag>,
    },
    (() => {
      const estadoId = salida.estado_del_descuento ?? final.estado_del_descuento;
      if (estadoId === null || estadoId === undefined) return false;
      const colorMap = {
        [ESTADOS_DEL_DESCUENTO.VIGENTE]: 'processing',
        [ESTADOS_DEL_DESCUENTO.APLICADO]: 'success',
        [ESTADOS_DEL_DESCUENTO.CANCELADO]: 'error',
        [ESTADOS_DEL_DESCUENTO.VENCIDO]: 'warning',
      };
      const labelMap = {
        [ESTADOS_DEL_DESCUENTO.VIGENTE]: 'Vigente',
        [ESTADOS_DEL_DESCUENTO.APLICADO]: 'Aplicado',
        [ESTADOS_DEL_DESCUENTO.CANCELADO]: 'Cancelado',
        [ESTADOS_DEL_DESCUENTO.VENCIDO]: 'Vencido',
      };
      return {
        key: 'estado_descuento',
        campo: 'Estado del Descuento',
        valor: (
          <Tag color={colorMap[estadoId] || 'default'}>
            {labelMap[estadoId] || `#${estadoId}`}
          </Tag>
        ),
      };
    })(),
    final.cargo && {
      key: 'cargo_desc',
      campo: 'Cargo Asociado',
      valor: <Tag color="default">{final.cargo}</Tag>,
    },
    (salida.importe_total !== null && salida.importe_total !== undefined) && {
      key: 'importe_total_desc',
      campo: 'Importe Total',
      valor: (
        <Text strong style={{ fontSize: 15 }}>
          $
          {fmt2(salida.importe_total)}
          {' '}
          <Text type="secondary" style={{ fontSize: 12 }}>MXP</Text>
        </Text>
      ),
    },
    (salida.saldo_pendiente_del_descuento !== null
      && salida.saldo_pendiente_del_descuento !== undefined) && {
      key: 'saldo_pendiente',
      campo: 'Saldo Pendiente',
      valor: `$${fmt2(salida.saldo_pendiente_del_descuento)} MXP`,
    },
    salida.fecha_de_aplicacion && {
      key: 'fecha_aplicacion',
      campo: 'Fecha de Aplicación',
      valor: new Date(salida.fecha_de_aplicacion).toLocaleString('es-MX'),
    },
    salida.fecha_de_vencimiento && {
      key: 'fecha_vencimiento',
      campo: 'Fecha de Vencimiento',
      valor: new Date(salida.fecha_de_vencimiento).toLocaleString('es-MX'),
    },
    (salida.usuario_creo || salida.usuario_que_aplico) && {
      key: 'usuario_aplico',
      campo: 'Aplicado Por',
      valor: salida.usuario_que_aplico || salida.usuario_creo,
    },
    salida.usuario_que_autorizo && {
      key: 'usuario_autorizo',
      campo: 'Autorizado Por',
      valor: salida.usuario_que_autorizo,
    },
    (final.comentarios || salida.comentarios) && {
      key: 'comentarios_desc',
      campo: 'Comentarios',
      valor: final.comentarios || salida.comentarios,
    },
    ...(filasVariables.length > 0
      ? [
        {
          key: '_header_variables', campo: 'VARIABLES APLICADAS', valor: null, esSeccion: true,
        },
        ...filasVariables,
      ]
      : []),
  ].filter(Boolean);
};

const transformarAlerta = (final = {}, salida = {}, formatEstadoGlobal) => {
  const tipoAlerta = (typeof salida.tipo_de_alerta === 'object' && salida.tipo_de_alerta !== null)
    ? salida.tipo_de_alerta : {};

  return [
    {
      key: '_header_alerta',
      campo: 'INFORMACIÓN DE LA ALERTA',
      valor: null,
      esSeccion: true,
    },
    final.clave && {
      key: 'folio_alerta',
      campo: 'Folio del trámite',
      valor: <Tag color="blue">{final.clave}</Tag>,
    },
    salida.folio && {
      key: 'folio_alerta',
      campo: 'Folio de la alerta',
      valor: <Tag color="blue">{salida.folio}</Tag>,
    },
    (tipoAlerta.descripcion || final.tipo_de_alerta) && {
      key: 'tipo_alerta',
      campo: 'Tipo de Alerta',
      valor: tipoAlerta.descripcion || `#${final.tipo_de_alerta}`,
    },
    (salida.descripcion || final.descripcion) && {
      key: 'descripcion_alerta',
      campo: 'Descripción',
      valor: salida.descripcion || final.descripcion,
    },
    (salida.observaciones || final.observaciones) && {
      key: 'observaciones_alerta',
      campo: 'Observaciones',
      valor: salida.observaciones || final.observaciones,
    },
    salida.observaciones_internas && {
      key: 'obs_internas',
      campo: 'Observaciones Internas',
      valor: salida.observaciones_internas,
    },
    {
      key: 'es_informativa',
      campo: 'Es Informativa',
      valor: boolTag(tipoAlerta.es_informativa ?? final.es_informativa),
    },
    {
      key: 'es_restrictiva',
      campo: 'Es Restrictiva',
      valor: boolTag(salida.es_restrictiva ?? final.es_restrictiva, 'error'),
    },
    (tipoAlerta.origen_de_alerta || final.origen_de_alerta) && {
      key: 'origen_alerta',
      campo: 'Origen de Alerta',
      valor: tipoAlerta.origen_de_alerta || final.origen_de_alerta,
    },
    {
      key: 'permite_cobrar_cargos',
      campo: 'Permite Cobrar Cargos',
      valor: boolTag(
        tipoAlerta.permite_cobrar_cargos ?? final.permite_cobrar_cargos, 'warning',
      ),
    },
    {
      key: 'aplica_padron_suspedido',
      campo: 'Aplica Padrón Suspendido',
      valor: boolTag(
        tipoAlerta.aplica_padron_suspedido ?? final.aplica_padron_suspedido, 'warning',
      ),
    },
    (salida.fecha_de_registro_de_alerta || final.fecha_de_registro_de_alerta) && {
      key: 'fecha_registro',
      campo: 'Fecha de Registro',
      valor: new Date(
        salida.fecha_de_registro_de_alerta || final.fecha_de_registro_de_alerta,
      ).toLocaleDateString('es-MX'),
    },
    (salida.estados_globales !== null && salida.estados_globales !== undefined) && {
      key: 'estado_alerta',
      campo: 'Estado',
      valor: formatEstadoGlobal(salida.estados_globales),
    },
  ].filter(Boolean);
};

const transformarOrdenDePago = (ordenData) => {
  if (!ordenData || typeof ordenData !== 'object') return [];
  const camposPrincipales = [
    { key: 'folio', campo: 'Folio', valor: ordenData.folio },
    {
      key: 'fecha_expedicion',
      campo: 'Fecha de Expedición',
      valor: ordenData.fecha_expedicion
        ? new Date(ordenData.fecha_expedicion).toLocaleString('es-MX') : null,
    },
    { key: 'contribuyente', campo: 'Contribuyente', valor: ordenData.contribuyente },
    { key: 'cuenta_catastral', campo: 'Cuenta Catastral', valor: ordenData.cuenta_catastral },
    {
      key: 'ubicacion_inmueble',
      campo: 'Ubicación del Inmueble',
      valor: ordenData.ubicacion_inmueble,
    },
    {
      key: 'base_gravable',
      campo: 'Base Gravable',
      valor: ordenData.base_gravable ? `$${fmt2(ordenData.base_gravable)}` : null,
    },
    {
      key: 'importe_total',
      campo: 'Importe Total',
      valor: ordenData.importe_total ? `$${fmt2(ordenData.importe_total)}` : null,
    },
    {
      key: 'fecha_hora_registro',
      campo: 'Fecha y Hora de Registro',
      valor: ordenData.fecha_hora_registro
        ? new Date(ordenData.fecha_hora_registro).toLocaleString('es-MX') : null,
    },
    {
      key: 'usuario_genero',
      campo: 'Usuario que Generó',
      valor: cleanPkString(ordenData.usuario_genero_nombre) || null,
    },
  ].filter(({ valor }) => valor !== null && valor !== undefined && valor !== '');

  const conceptos = ordenData.conceptos || [];
  const filasConceptos = conceptos.map((concepto, index) => ({
    key: `concepto_${index}`,
    campo: `Concepto ${index + 1}`,
    valor: (
      <div style={{ whiteSpace: 'normal' }}>
        <div>
          <strong>Descripción:</strong>
          {concepto.concepto || '—'}
        </div>
        <div>
          <strong>Importe:</strong>
          $
          {fmt2(concepto.importe)}
        </div>
        <div>
          <strong>Clave:</strong>
          {concepto.clave_concepto || '—'}
        </div>
        {concepto.cargo_id && (
          <div>
            <strong>Cargo ID:</strong>
            {' '}
            {concepto.cargo_id}
          </div>
        )}
      </div>
    ),
  }));

  return [
    ...camposPrincipales,
    ...(conceptos.length > 0
      ? [{
        key: 'conceptos_header',
        campo: 'Conceptos',
        valor: `(${conceptos.length} conceptos)`,
      }]
      : []),
    ...filasConceptos,
  ];
};

const ModalComparacionTramite = ({
  visible,
  onClose,
  titulo = 'Comparación de Información',
  inicial = {},
  final = {},
  ordenDePago = {},
  salida = {},
}) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [contribuyentes, setContribuyentes] = useState({});
  const cats = useCatalogos();

  const formatEstadoGlobal = (valor) => {
    if (valor === null || valor === undefined) return '—';
    const limpio = cleanPkString(valor);
    const porId = (estadosGlobales || []).find((e) => String(e.id) === String(limpio));
    if (porId) return porId.descripcion || porId.clave || porId.nombre;
    const txt = String(limpio).toLowerCase();
    const porTexto = (estadosGlobales || [])
      .find((e) => [e.descripcion, e.clave, e.nombre]
        .some((x) => x && String(x).toLowerCase() === txt));
    return porTexto ? (porTexto.descripcion || porTexto.clave || porTexto.nombre) : limpio;
  };

  const catalogFmts = Object.fromEntries(
    CATALOG_FIELDS.map(({ key, getCat, keys }) => [key, mkFmt(getCat(cats), keys)]),
  );

  const FORMATTERS = {
    ...catalogFmts,
    notario_predio: (v) => {
      const info = parseNotarioInfo(v);
      if (!info) return '—';
      return [info.nombre, info.clave, info.id != null ? `#${info.id}` : null]
        .filter(Boolean).join(' | ');
    },
    estados_globales: formatEstadoGlobal,
    contribuyente: (v) => formatContribuyenteValue(v, contribuyentes),
    idPadron: formatIdPadron,
    padron: formatIdPadron,
    json_predio: formatJsonPredio,
    attrs: formatJsonPredio,
  };

  const NORMALIZERS = {
    ...Object.fromEntries(
      Object.entries(catalogFmts).map(([key, fmt]) => [key, mkNorm(fmt)]),
    ),
    estados_globales: mkNorm(formatEstadoGlobal),
    notario_predio: (v) => {
      const info = parseNotarioInfo(v);
      return (info?.id != null) ? String(info.id) : fallbackNormalize(v);
    },
    idPadron: (v) => {
      const limpio = cleanPkString(v);
      try {
        const entry = Object.entries(padronesEnum)
          .find(([, id]) => String(id) === String(limpio));
        return (entry ? entry[0] : String(limpio)).toLowerCase();
      } catch { return String(limpio).toLowerCase(); }
    },
    json_predio: (v) => {
      const obj = tryParseJSON(v);
      return typeof obj === 'object' && obj !== null ? JSON.stringify(obj) : String(v ?? '');
    },
    direccion: (v) => canonDireccion(
      (v && typeof v === 'object') ? buildDireccionString(v) : v,
    ),
    contribuyente: (v) => {
      let rfc = extractRFC(v);
      if (!rfc) {
        const id = extractId(v);
        const c = id ? contribuyentes[id] : null;
        rfc = c ? extractRFC(c) : null;
      }
      return rfc ? rfc.trim().toUpperCase()
        : String(cleanPkString(v) ?? '').trim().toLowerCase();
    },
  };

  const normalizeForDiff = (key, value) => {
    if (DECIMAL_KEYS.has(key)) {
      const n = toNumber(value);
      return n === null ? null : n;
    }
    return (NORMALIZERS[key] || fallbackNormalize)(value);
  };

  useEffect(() => {
    const ids = [inicial?.contribuyente, final?.contribuyente]
      .map(extractId).filter((id) => id && !contribuyentes[id]);
    if (ids.length === 0) return;
    (async () => {
      const pares = await Promise.all(ids.map(async (id) => {
        try { return [id, await getContribuyente(id)]; } catch { return null; }
      }));
      const next = { ...contribuyentes };
      pares.filter(Boolean).forEach(([id, data]) => { next[id] = data; });
      setContribuyentes(next);
    })();
  }, [visible, inicial?.contribuyente, final?.contribuyente]);

  const accionInf = detectarAccionInf(final, inicial);
  const esCancelacion = accionInf === 'CANCELAR_CARGO' || accionInf === 'CARGO_PRESCRIPTO'
    || accionInf === 'CARGO_EXENTO';
  const esDescuento = accionInf === 'DESCUENTOS';
  const esAlerta = (
    accionInf === 'CREAR_ALERTAS_EN_TRAMITE' || accionInf === 'CREAR_ALERTAS'
    || accionInf === 'EDITAR_ALERTAS_EN_TRAMITE' || accionInf === 'EDITAR_ALERTAS'
  );
  const esOrdenDePago = !esCancelacion && !esDescuento && !esAlerta
    && ordenDePago && Object.keys(ordenDePago).length > 0;
  const esCreacion = !esCancelacion && !esDescuento && !esAlerta
    && Object.keys(inicial || {}).length === 0 && Object.keys(final || {}).length > 0;

  const dataSource = useMemo(() => {
    if (esCancelacion) return transformarCancelacion(inicial, final);
    if (esDescuento) return transformarDescuento(final, salida);
    if (esAlerta) return transformarAlerta(final, salida, formatEstadoGlobal);
    if (esOrdenDePago) return transformarOrdenDePago(ordenDePago);

    if (esCreacion) {
      return procesarCampos(Object.keys(final), (key) => {
        const valorFinal = final[key];
        const formatFn = FORMATTERS[key] || formatearValor;
        const formateado = DECIMAL_KEYS.has(key) ? fmt2(valorFinal) : formatFn(valorFinal);
        if (formateado === '—') return null;
        return { key, campo: labelDeCampo(key), valor: formateado };
      });
    }

    return procesarCampos(getCamposUnicos(inicial, final), (key) => {
      const valorInicial = inicial?.[key];
      const valorFinal = final?.[key];
      const formatFn = FORMATTERS[key] || formatearValor;
      let formateadoInicial = DECIMAL_KEYS.has(key) ? fmt2(valorInicial) : formatFn(valorInicial);
      let formateadoFinal = DECIMAL_KEYS.has(key) ? fmt2(valorFinal) : formatFn(valorFinal);
      let normIni = normalizeForDiff(key, valorInicial);
      let normFin = normalizeForDiff(key, valorFinal);
      const esFinalVacio = valorFinal === null || valorFinal === undefined || valorFinal === '';
      if (key === 'notario_predio') {
        const ini = parseNotarioInfo(valorInicial);
        const fin = parseNotarioInfo(valorFinal);
        if (ini?.id != null && fin?.id != null && String(ini.id) === String(fin.id)) {
          const display = ini.nombre || ini.clave || String(ini.id);
          formateadoInicial = display;
          formateadoFinal = display;
          normIni = String(ini.id);
          normFin = String(fin.id);
        }
      }
      if (esFinalVacio || (formateadoInicial === '—' && formateadoFinal === '—')) return null;
      return {
        key,
        campo: labelDeCampo(key),
        valor_inicial: formateadoInicial,
        valor_final: formateadoFinal,
        haCambiado: normIni !== normFin,
      };
    });
  }, [inicial, final, contribuyentes, estadosGlobales, ordenDePago,
    esCancelacion, esDescuento, esAlerta, esOrdenDePago, esCreacion]);

  const columns = (esCancelacion || esOrdenDePago || esDescuento || esAlerta || esCreacion)
    ? COLUMNAS_CAMPO_VALOR : COLUMNAS_COMPARACION;

  const onRow = (row) => ({
    style: row.esSeccion
      ? { backgroundColor: '#fafafa', borderTop: '2px solid #e8e8e8', pointerEvents: 'none' }
      : {},
  });

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      onOk={onClose}
      title={titulo}
      width="100%"
      footer={null}
    >
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        size="small"
        bordered
        scroll={{ y: 450 }}
        onRow={onRow}
      />
    </Modal>
  );
};

ModalComparacionTramite.defaultProps = {
  titulo: 'Comparación de Información',
  inicial: {},
  final: {},
  ordenDePago: {},
  salida: {},
};

ModalComparacionTramite.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  titulo: PropTypes.string,
  inicial: PropTypes.shape({}),
  final: PropTypes.shape({}),
  ordenDePago: PropTypes.shape({}),
  salida: PropTypes.shape({}),
};

export default ModalComparacionTramite;
